/**
 * The percent chips and the slider.
 *
 * "50%" and "MAX" are the same control at different stops, so they must agree
 * exactly: the chip that wrote a value has to light up again when the field
 * holds it. That only works if the fraction is computed on the grid, with exact
 * decimals — `0.58 * 0.5` is 0.29 here, and 100% hands back the maximum
 * untouched rather than a value the step happened to floor away.
 */
import { Dec, dec } from "./decimal";

/** Percent, clamped to 0..100. Anything that is not a number reads as 0. */
const clampPct = (pct: number): number => {
  if (!Number.isFinite(pct)) return 0;
  if (pct <= 0) return 0;
  return pct > 100 ? 100 : pct;
};

/** A positive maximum as canonical text, or `null`. Negative is not a maximum. */
const positiveMax = (max: Dec | number): Dec | null => {
  const parsed = dec.parse(max);
  return parsed !== null && dec.gt(parsed, "0") ? parsed : null;
};

/**
 * `pct` percent of `max`, floored onto `step`.
 *
 * 100% returns `max` EXACTLY — never floored — because the whole balance is
 * spendable even when it does not sit on the market's grid (0.00000148 BTC on a
 * 0.001 step is still 0.00000148 BTC to withdraw, and a full close is the
 * position's own size). Every other stop floors, so a chip can only ever ask for
 * less than the trader has.
 *
 * Returns "0" when there is nothing to take a fraction of.
 */
export const fractionOfMax = (max: Dec, pct: number, step: Dec): Dec => {
  const ceiling = positiveMax(max);
  if (ceiling === null) return "0";
  const percent = clampPct(pct);
  if (percent === 0) return "0";
  if (percent >= 100) return ceiling;
  const raw = dec.div(dec.mul(ceiling, percent), "100", 40, "floor") ?? "0";
  // An unusable step (0, absent, not a number) leaves the fraction alone rather
  // than zeroing it: a grid we cannot read is not a reason to offer nothing.
  return dec.toStep(raw, step, "floor") ?? raw;
};

/**
 * Where `value` sits on a 0..100 slider. UI only — the returned number is a
 * position, never an amount headed on-chain.
 */
export const percentOfMax = (value: Dec, max: Dec): number => {
  const ceiling = positiveMax(max);
  if (ceiling === null) return 0;
  const ratio = dec.div(dec.mul(value, "100"), ceiling, 12, "floor");
  if (ratio === null) return 0;
  const asNumber = dec.toNumber(ratio);
  if (!Number.isFinite(asNumber) || asNumber <= 0) return 0;
  return asNumber > 100 ? 100 : asNumber;
};

/**
 * Which chip is lit: the first preset whose own output equals `value`, or `null`
 * when the field holds something no chip wrote.
 *
 * Comparing against `fractionOfMax` rather than against a percentage means a
 * coarse step cannot un-light the chip that produced the value — 50% of 9.99 on
 * a 1 grid writes "4", and "4" lights 50%. A zero or empty field lights nothing;
 * several presets can collapse onto the same value on a coarse grid, and the
 * first of them wins.
 */
export const activePreset = (
  value: Dec,
  max: Dec,
  presets: readonly number[],
  step: Dec
): number | null => {
  const parsed = dec.parse(value);
  if (parsed === null || !dec.gt(parsed, "0")) return null;
  if (positiveMax(max) === null) return null;
  for (const preset of presets) {
    const atPreset = fractionOfMax(max, preset, step);
    if (dec.gt(atPreset, "0") && dec.eq(atPreset, parsed)) return preset;
  }
  return null;
};
